import { ChangeDetectorRef, Component, ElementRef, Injector, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, AutomaticResponseUtil, Channel, FirebaseService, MessageTypeEnum, Pager, Response, ResponseService } from 'lib-trend-core';
import { formatChatFileSize } from 'lib-trend-core/chat';
import { Observable } from 'rxjs';
import { ResponseAttachment, ResponseCaptionDialogComponent, ResponseCaptionDialogData, ResponseCaptionDialogResult } from '../response-caption-dialog/response-caption-dialog.component';
import { ResponseMessageUtil, ResponseTextFormat } from '../response-message.util';

// JSON replacer that drops null fields, so a field cleared on screen equals one never set.
const withoutEmpty = (_key: string, value: unknown): unknown => value ?? undefined;

@Component({
    selector: 'response-automatic-component',
    // templateUrl: './response-automatic.component.html'
    templateUrl: './response-automatic.v2.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ResponseAutomaticComponent extends AbstractComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('messageTextarea') messageTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild("emojiContainer", { static: false, read: ElementRef }) emojiContainer!: ElementRef
  @ViewChild('imageInput') imageInput: ElementRef<HTMLInputElement>;
  @ViewChild('documentInput') documentInput: ElementRef<HTMLInputElement>;

  @Input() channel: Channel;

  readonly imageType = MessageTypeEnum.image;
  readonly documentType = MessageTypeEnum.document;

  list: Array<Response> = new Array<Response>();
  listObservable: Observable<Pager<Response>>;

  showContainerEmojiIndex!: number | null;

    originalList: Array<Response> = [];

  private clearTimeout;
  private clickOutsideListener;

  // Card the attachment menu is picking a file for.
  private pickingIndex: number | null = null;
  // Files uploaded on this screen and not saved yet.
  private readonly sessionUploads = new Set<string>();

  constructor(
    injector: Injector,
    private cdRef: ChangeDetectorRef,
    private responseService: ResponseService,
    private renderer: Renderer2,
    private dialog: MatDialog,
    private firebaseService: FirebaseService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes['channel']?.currentValue) {
      this.channel = changes['channel']?.currentValue;
      this.getList();
    }
  }

  onItemChange(): void {
    this.cdRef.detectChanges();
  }

  private getList() {
    this.responseService.getByChannel(this.channel._id).subscribe({
      next: (responses: Array<Response>) => {
        this.list = responses;
        // Save a deep copy of the original state
        this.originalList = JSON.parse(JSON.stringify(responses));
      },
    });
  }

  updateAll(): void {
    this.loading = true;
    const allUpdates: any = this.list.map(item => ({
      ...item,
      message: item.message,
      activated: item.activated,
    }));
    this.responseService.updateAll(this.channel._id, allUpdates).subscribe({
      next: () => {
        this.alertService.success(this.t('response.automatic.updateSuccess'));
        this.cdRef.detectChanges();
        this.loading = false;
        // The saved state becomes the new baseline
        this.originalList = JSON.parse(JSON.stringify(this.list));
      },
      error: (err) => {
        this.loading = false;
        this.alertService.error(err.error?.message);
      },
    });
  }

  hasChanges(): boolean {
    // Compares the current list with the saved one
    return JSON.stringify(this.list, withoutEmpty) !== JSON.stringify(this.originalList, withoutEmpty);
  }

  get acceptsAttachment(): boolean {
    return AutomaticResponseUtil.acceptsAttachment(this.channel?.type);
  }

  get captionLimit(): number | null {
    return AutomaticResponseUtil.captionLimit(this.channel?.type);
  }

  acceptFor(type: MessageTypeEnum): string {
    return AutomaticResponseUtil.accept(type);
  }

  isImage(item: Response): boolean {
    return AutomaticResponseUtil.attachmentType(item.contentType) === MessageTypeEnum.image;
  }

  extensionOf(item: Response): string {
    return AutomaticResponseUtil.extension(item.filename);
  }

  attachmentMeta(item: Response): string {
    const label = this.t(this.isImage(item) ? 'response.automatic.attachment.image' : 'response.automatic.attachment.document');
    return item.filesize ? `${label} · ${formatChatFileSize(item.filesize)}` : label;
  }

  pickFile(index: number, type: MessageTypeEnum): void {
    this.pickingIndex = index;
    const input = type === MessageTypeEnum.image ? this.imageInput : this.documentInput;
    input.nativeElement.click();
  }

  onFileChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // lets the same file be picked again after a rejection
    if (file && this.pickingIndex !== null && this.isAccepted(file)) {
      this.openCaptionDialog(this.pickingIndex, file);
    }
  }

  editCaption(index: number): void {
    this.openCaptionDialog(index);
  }

  removeAttachment(index: number): void {
    this.discardUnsaved(index);
    this.setAttachment(this.list[index], null);
  }

  toogleContainerEmoji(index: number): void {
    if (this.showContainerEmojiIndex === index) {
      this.showContainerEmojiIndex = null;
    } else {
      this.showContainerEmojiIndex = index;
    }

    clearTimeout(this.clearTimeout)

    this.clearTimeout = setTimeout(() => {
      this.addClickOutsideListener();
    })
  }

  addClickOutsideListener() {
    this.clickOutsideListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      if (this.showContainerEmojiIndex >= 0 && this.emojiContainer) {
        const modalElement = this.emojiContainer.nativeElement as HTMLElement;
        const clickedInside = modalElement?.contains(event.target as Node);

        if (!clickedInside) {
          this.closedContainerEmoji();
        }
      }
    });
  }

  closedContainerEmoji(): void {
    this.showContainerEmojiIndex = null;
    this.clickOutsideListener()
    this.clickOutsideListener = null
  }

  addEmoji(event: any, index: number): void {
    const item = this.list[index];
    item.message = ResponseMessageUtil.appendEmoji(item.message, event.emoji.native);
  }

  getStyleContainerEmoji(index: number): string {
    switch (index) {
      case 0:
        return 'absolute bottom-75 left-[9.625rem] z-50';
      case 1:
        return 'absolute bottom-75 left-[39.5rem] z-50';
      case 2:
        return 'absolute bottom-75 left-[69.5rem] z-50';
      case 3:
        return 'absolute -bottom-70 left-[9.625rem] z-50';
      case 4:
        return 'absolute -bottom-70 left-[39.5rem] z-50'
      default:
        return 'absolute -bottom-70 left-[69.5rem] z-50';
    }
  }

  setVariableInContent(variable: string, index: number): void {
    const item = this.list[index];
    item.message = ResponseMessageUtil.appendVariable(item.message, variable);
  }

  applyFormat(format: ResponseTextFormat, index: number, textarea: HTMLTextAreaElement): void {
    const item = this.list[index];
    const formatted = ResponseMessageUtil.format(item.message, textarea.selectionStart, textarea.selectionEnd, format);
    if (!formatted) return;
    item.message = formatted.message;
    this.cdRef.detectChanges();
    textarea.setSelectionRange(formatted.caret, formatted.caret);
    textarea.focus();
  }

  private openCaptionDialog(index: number, file?: File): void {
    const item = this.list[index];
    const data: ResponseCaptionDialogData = {
      responseName: item.header,
      channelType: this.channel?.type,
      message: item.message,
      file,
      attachment: file ? undefined : { midia: item.midia, contentType: item.contentType, filename: item.filename, filesize: item.filesize },
    };
    this.dialog.open<ResponseCaptionDialogComponent, ResponseCaptionDialogData, ResponseCaptionDialogResult>(ResponseCaptionDialogComponent, { width: '640px', data })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.applyCaption(index, result);
        }
      });
  }

  private applyCaption(index: number, result: ResponseCaptionDialogResult): void {
    if (result.uploaded) {
      this.discardUnsaved(index);
      this.sessionUploads.add(result.attachment.midia);
    }
    this.list[index].message = result.message;
    this.setAttachment(this.list[index], result.attachment);
  }

  private setAttachment(item: Response, attachment: ResponseAttachment | null): void {
    item.midia = attachment?.midia ?? null;
    item.contentType = attachment?.contentType ?? null;
    item.filename = attachment?.filename ?? null;
    item.filesize = attachment?.filesize ?? null;
    this.onItemChange();
  }

  // A file uploaded here and not saved yet is deleted when replaced or removed. The saved one never
  // is, so discarding the changes never leaves the response pointing at a missing file.
  private discardUnsaved(index: number): void {
    const midia = this.list[index].midia;
    if (midia && this.sessionUploads.has(midia) && midia !== this.originalList[index]?.midia) {
      void this.firebaseService.deleteFile(midia);
      this.sessionUploads.delete(midia);
    }
  }

  private isAccepted(file: File): boolean {
    if (!AutomaticResponseUtil.attachmentType(file.type)) {
      this.alertService.warning(this.t('response.automatic.attachment.typeError'));
      return false;
    }
    const maxSize = AutomaticResponseUtil.maxSize(file.type);
    if (maxSize !== null && file.size > maxSize) {
      this.alertService.warning(this.t('response.automatic.attachment.sizeError', { size: maxSize / (1024 * 1024) }));
      return false;
    }
    return true;
  }

  updateMessage() {
    const textarea = this.messageTextarea.nativeElement as HTMLTextAreaElement;
    const messageWithoutLineBreaks = textarea.value;
    this.formGroup.patchValue({ message: messageWithoutLineBreaks });
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    let pastedData = clipboardData.getData('text/html') || clipboardData.getData('text/plain');

    pastedData = pastedData
      .replace(/<p[^>]*>/g, '\n')
      .replace(/<\/p>/g, '\n\n')
      .replace(/<li[^>]*>/g, '')
      .replace(/<\/li>/g, '\n')
      .replace(/<\/?html[^>]*>/g, '')
      .replace(/<\/?body[^>]*>/g, '')
      .replace(/<!--.*?-->/g, '')
      .replace(/<\/?br[^>]*>/g, '\n')
      .replace(/<\/?strong[^>]*>/g, '*')
      .replace(/<\/?b[^>]*>/g, '*')
      .replace(/<\/?em[^>]*>/g, '_')
      .replace(/<\/?i[^>]*>/g, '_')
      .replace(/<\/?u[^>]*>/g, '~$&~');

    pastedData = pastedData.replace(/\n{2,}/g, '\n\n').trim();

    const textarea = this.messageTextarea.nativeElement as HTMLTextAreaElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    textarea.value =
      textarea.value.substring(0, start) +
      pastedData +
      textarea.value.substring(end);

    textarea.setSelectionRange(start + pastedData.length, start + pastedData.length);
    textarea.focus();
    this.updateMessage();
  }

}
